import type { JournalLeg } from '../types'

/**
 * The greeks a trade was entered and exited at — ROADMAP 9.54.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO SCOPES, TWO UNITS, AND THE UNIT HAS TO BE ON SCREEN
 *
 * `Trade total` weights each leg's greek by its signed quantity, which makes
 * the figure a POSITION greek: rupees of decay per day, delta in units of
 * the underlying. Picking a single leg weights by one, which leaves the
 * PER-UNIT greek the option chain shows.
 *
 * Both are worth having and they differ by the position size — on a
 * 30,000-unit SENSEX basket, by four orders of magnitude. They were drawn
 * under the identical label "Theta / day", so one click turned -39 into
 * 11,73,139.76 with nothing on screen to say the unit had changed. Reported
 * on 2026-11-05: "in the option chain we show theta in tens, then how come
 * showing in tens lakhs in journal?"
 *
 * So the scope now carries its own name, unit and precision:
 *
 *   per unit    what one contract does. Small numbers, many decimals —
 *               a gamma of 0.00008 is zero at two places.
 *   position    what the whole basket does. Large numbers, no decimals —
 *               the paise on ₹11,73,139.76 are noise, and printing them
 *               implies a precision the model does not have.
 *
 * IV IS THE EXCEPTION and is never scaled: a volatility is a rate, not an
 * exposure, and multiplying it by a quantity produces nothing. The trade
 * figure is the mean of the legs that recorded one.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type SnapshotScope = 'TRADE' | 'LEG'

interface MetricScope {
  /** What the figure IS at this scope, including its unit. */
  readonly name: string
  readonly digits: number
  /**
   * Is this figure RUPEES at this scope? — ROADMAP 9.56.
   *
   * Position theta and vega are money: so many rupees of decay a day, so
   * many per point of implied volatility. They were drawn as bare grouped
   * numbers — `11,73,140` — in a panel where every other rupee figure on
   * the page reads `+₹2.72 L`, so the one number that most needed a sense
   * of scale was the one without it.
   *
   * Delta and gamma are NOT money at any scope. Delta is a quantity of the
   * underlying and gamma is delta per point; putting a rupee sign on
   * either would name a unit they do not have.
   */
  readonly currency?: true
}

export const SNAPSHOT_METRICS = [
  {
    key: 'iv',
    label: 'IV',
    entry: 'entryIv',
    exit: 'exitIv',
    // Not an exposure, so it does not scale and reads the same either way.
    trade: { name: 'Implied volatility', digits: 1 },
    leg: { name: 'Implied volatility', digits: 1 },
  },
  {
    key: 'delta',
    label: 'Δ',
    entry: 'entryDelta',
    exit: 'exitDelta',
    trade: { name: 'Delta · underlying units', digits: 0 },
    leg: { name: 'Delta · per unit', digits: 3 },
  },
  {
    key: 'gamma',
    label: 'Γ',
    entry: 'entryGamma',
    exit: 'exitGamma',
    trade: { name: 'Gamma · Δ per point', digits: 2 },
    leg: { name: 'Gamma · per unit', digits: 5 },
  },
  {
    key: 'theta',
    label: 'Θ',
    entry: 'entryTheta',
    exit: 'exitTheta',
    trade: { name: 'Theta · per day', digits: 0, currency: true },
    leg: { name: 'Theta · per unit, per day', digits: 2 },
  },
  {
    key: 'vega',
    label: 'ν',
    entry: 'entryVega',
    exit: 'exitVega',
    trade: { name: 'Vega · per 1% IV', digits: 0, currency: true },
    leg: { name: 'Vega · per unit, per 1% IV', digits: 2 },
  },
] as const satisfies readonly {
  key: string
  label: string
  entry: keyof JournalLeg
  exit: keyof JournalLeg
  trade: MetricScope
  leg: MetricScope
}[]

type SnapshotLeg = Pick<JournalLeg, 'qty' | 'side' | (typeof SNAPSHOT_METRICS)[number]['entry' | 'exit']>

/** A missing leg must not turn an incomplete exposure into a complete total. */
export function snapshotComparison(legs: readonly SnapshotLeg[], scope: SnapshotScope) {
  return SNAPSHOT_METRICS.map((metric) => {
    const presentation: MetricScope = scope === 'TRADE' ? metric.trade : metric.leg
    const read = (field: typeof metric.entry | typeof metric.exit) => {
      let total = 0
      let recorded = 0
      for (const leg of legs) {
        const value = leg[field]
        if (typeof value !== 'number' || !Number.isFinite(value)) continue
        recorded += 1
        const weight = metric.key === 'iv' || scope === 'LEG' ? 1 : leg.qty * (leg.side === 'BUY' ? 1 : -1)
        total += value * weight
      }
      const value = recorded === 0 || recorded !== legs.length
        ? null
        : metric.key === 'iv' ? total / recorded * 100 : total
      return { value, recorded }
    }
    return {
      key: metric.key,
      label: metric.label,
      name: presentation.name,
      digits: presentation.digits,
      /** Render with the page's rupee format rather than a bare number. */
      currency: presentation.currency === true,
      /** True when the figure is scaled by position size — see the header. */
      position: scope === 'TRADE' && metric.key !== 'iv',
      entry: read(metric.entry),
      exit: read(metric.exit),
    }
  })
}
